import {
  type CreatePackageHotelRequest,
  type PackageDetail,
  type PackageHotelOut,
} from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { BedDouble, ChevronDown, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useAddPackageHotel, useDeletePackageHotel, useUpdatePackageHotel } from '../use-packages';

import { PackageHotelFormDrawer } from './PackageHotelFormDrawer';

interface HotelsEditorProps {
  /** Live (edit) mode: the existing package whose hotels are edited via the API. */
  pkg?: PackageDetail | null;
  /** Controlled (create) mode: staged hotels held in the parent. */
  draftValue?: CreatePackageHotelRequest[];
  /** Controlled (create) mode: provide to switch to in-memory editing (no API). */
  onDraftChange?: (items: CreatePackageHotelRequest[]) => void;
}

/** Build a {@link PackageHotelOut}-shaped row from a draft hotel for the list + edit drawer. */
const draftToRow = (h: CreatePackageHotelRequest, i: number): PackageHotelOut => ({
  id: `draft-${i}`,
  hotelId: h.hotelId ?? null,
  cityName: h.cityName ?? null,
  hotelName: h.hotelName ?? null,
  category: h.category ?? null,
  nights: h.nights ?? null,
  occupancy: h.occupancy ?? null,
  sortOrder: h.sortOrder ?? i,
});

export const HotelsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: HotelsEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftItems = draftValue ?? [];

  // Hooks run unconditionally; in controlled mode the mutations never fire.
  const add = useAddPackageHotel(pkg?.id ?? '');
  const update = useUpdatePackageHotel(pkg?.id ?? '');
  const del = useDeletePackageHotel(pkg?.id ?? '');
  const [drawer, setDrawer] = useState<{
    mode: 'create' | 'edit';
    item?: PackageHotelOut;
    draftIndex?: number;
  } | null>(null);

  const rows: PackageHotelOut[] = controlled ? draftItems.map(draftToRow) : (pkg?.hotels ?? []);
  const pending = controlled ? false : add.isPending || update.isPending || del.isPending;

  const submit = async (payload: CreatePackageHotelRequest): Promise<void> => {
    if (controlled) {
      const next =
        drawer?.mode === 'edit' && drawer.draftIndex !== undefined
          ? draftItems.map((d, i) => (i === drawer.draftIndex ? payload : d))
          : [...draftItems, payload];
      onDraftChange(next.map((h, i) => ({ ...h, sortOrder: i })));
      setDrawer(null);
      return;
    }
    if (drawer?.mode === 'edit' && drawer.item) {
      await update.mutateAsync({ childId: drawer.item.id, payload });
    } else {
      await add.mutateAsync(payload);
    }
    setDrawer(null);
  };

  const remove = (row: PackageHotelOut, i: number): void => {
    if (!window.confirm('Remove this hotel?')) return;
    if (controlled) {
      onDraftChange(
        draftItems.filter((_, idx) => idx !== i).map((h, idx) => ({ ...h, sortOrder: idx })),
      );
      return;
    }
    del.mutate(row.id);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Hotels</h3>
        <UzButton size="sm" onClick={() => setDrawer({ mode: 'create' })} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add hotel
        </UzButton>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No hotels yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row, i) => {
            const isOpen = drawer?.mode === 'edit' && drawer.draftIndex === i;
            const toggle = (): void =>
              setDrawer(isOpen ? null : { mode: 'edit', item: row, draftIndex: i });
            return (
              <React.Fragment key={row.id}>
                <li
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-zinc-200 px-3 py-2 hover:bg-zinc-50"
                >
                  <BedDouble className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                    {row.hotelName ?? 'Hotel'}
                  </span>
                  {row.cityName && (
                    <span className="shrink-0 text-xs text-zinc-500">{row.cityName}</span>
                  )}
                  {row.category && (
                    <span className="shrink-0 text-xs text-zinc-500">{row.category}</span>
                  )}
                  {row.nights !== null && row.nights !== undefined && (
                    <span className="shrink-0 text-xs text-zinc-500 tabular-nums">
                      {row.nights}N
                    </span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(row, i);
                    }}
                    disabled={pending}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove hotel"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                {isOpen && (
                  <li className="list-none">
                    <PackageHotelFormDrawer
                      mode="edit"
                      item={row}
                      pending={pending}
                      onClose={() => setDrawer(null)}
                      onSubmit={submit}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {drawer?.mode === 'create' && (
        <PackageHotelFormDrawer
          mode="create"
          pending={pending}
          onClose={() => setDrawer(null)}
          onSubmit={submit}
        />
      )}
    </section>
  );
};
